import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useNotifications } from '../hooks/useNotifications';
import './Alerts.css';

const Alerts = () => {
  const navigate = useNavigate();
  const { user, pickups } = useApp();
  const { 
    notifications, 
    groupedNotifications, 
    unreadCount, 
    isLoading,
    markAsRead, 
    markAllAsRead 
  } = useNotifications(pickups);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  const handleAlertClick = async (alert) => {
    if (!alert.read) {
      await markAsRead(alert.id);
    }
    if (alert.orderId) {
      navigate(`/pickup/${alert.orderId}`);
    }
  };

  const handleMarkAllAsReadClick = async () => {
    await markAllAsRead();
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'pickup': return '📦';
      case 'reminder': return '⏰';
      case 'payment': return '💰';
      default: return '🔔';
    }
  };

  const renderNotificationGroup = (title, notifications) => {
    if (notifications.length === 0) return null;

    return (
      <div key={title} className="notification-group">
        <div className="group-header">{title}</div>
        {notifications.map((alert) => (
          <div
            key={alert.id}
            className={`alert-item ${!alert.read ? 'unread' : ''}`}
            onClick={() => handleAlertClick(alert)}
          >
            <div className="alert-icon">
              {getAlertIcon(alert.type)}
            </div>
            <div className="alert-body">
              <div className="alert-top">
                <h4>{alert.title}</h4>
                {!alert.read && <span className="unread-dot" />}
              </div>
              <p className="alert-msg">{alert.message}</p>
              <span className="alert-time">{alert.timeAgo}</span>
            </div>
            {!alert.read && (
              <button
                className="mark-btn"
                onClick={async (e) => {
                  e.stopPropagation();
                  await markAsRead(alert.id);
                }}
                aria-label="Mark as read"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )}
            {alert.read && (
              <div className="arrow-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="alerts-page">
      <div className="alerts-content">
        {/* Header */}
        <header className="alerts-header">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1>Notifications</h1>
          {unreadCount > 0 && (
            <button className="mark-all-btn" onClick={handleMarkAllAsReadClick}>
              Mark all read
            </button>
          )}
          {unreadCount === 0 && <div style={{ width: 80 }} />}
        </header>

        {/* Unread count badge */}
        {unreadCount > 0 && (
          <div className="unread-badge">
            <span className="badge-count">{unreadCount}</span>
            <span className="badge-text">unread notification{unreadCount > 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Alerts List */}
        <div className="alerts-list">
          {isLoading ? (
            <div className="empty-state">
              <div className="loading-spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
                </svg>
              </div>
              <h3>All caught up!</h3>
              <p>No new notifications at the moment</p>
            </div>
          ) : (
            <>
              {renderNotificationGroup('Today', groupedNotifications.today)}
              {renderNotificationGroup('Yesterday', groupedNotifications.yesterday)}
              {renderNotificationGroup('This Week', groupedNotifications.thisWeek)}
              {renderNotificationGroup('Older', groupedNotifications.older)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Alerts;
